import React, { useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import multiMonthPlugin from '@fullcalendar/multimonth';
import timeGridPlugin from '@fullcalendar/timegrid';
import rrulePlugin from '@fullcalendar/rrule';
import { DatesSetArg } from '@fullcalendar/core';
import { Box, CircularProgress } from '@mui/material';
import { startOfWeek } from 'date-fns';
import { useImmer } from 'use-immer';
import { CALENDAR_VIEWS, getHeaderTitle, getNextStartDate, getPreviousStartDate } from './calendar-helpers';
import { CalendarView } from './calendar-types';
import { CalendarToolbar } from './calendar-toolbar';

interface CalendarState {
  currentStartDate: Date;
  currentCalendarView: CalendarView;
}

type FullCalendarProps = Omit<React.ComponentProps<typeof FullCalendar>, 'ref'>;

interface FasitCalendarWrapperProps extends FullCalendarProps {
  printTitle?: string;
  toolbarVariant?: 'v1' | 'v2';
  onVisibleRangeChange?: (range: { start: Date; end: Date }) => void;
  onAddEventClick?: () => void;
  isLoading?: boolean;
}

export function FasitCalendarWrapper(props: FasitCalendarWrapperProps) {
  const { printTitle, toolbarVariant = 'v1', onVisibleRangeChange, onAddEventClick, isLoading, ...fullCalendarProps } = props;
  const [state, setState] = useImmer<CalendarState>({
    currentStartDate: startOfWeek(new Date(), { weekStartsOn: 1 }),
    currentCalendarView: 'week',
  });
  const { currentStartDate, currentCalendarView } = state;
  const calendarRef = useRef<FullCalendar>(null);

  function handleNextClick() {
    const startDate = getNextStartDate(currentCalendarView, currentStartDate);
    goToDate(startDate);
  }

  function handlePreviousClick() {
    const startDate = getPreviousStartDate(currentCalendarView, currentStartDate);
    goToDate(startDate);
  }

  function handleTodayClick() {
    if (currentCalendarView === 'day') {
      goToDate(new Date());
      return;
    }

    goToDate(startOfWeek(new Date(), { weekStartsOn: 1 }));
  }

  function goToDate(startDate: Date) {
    setState(draft => {
      draft.currentStartDate = startDate;
      const api = calendarRef.current?.getApi();
      api?.gotoDate(startDate);
    });
  }

  function handleCalendarViewChange(view: CalendarView) {
    const api = calendarRef.current?.getApi();
    if (!api) {
      return;
    }

    let startDateForView: Date | null = null;
    switch (view) {
      case 'day': {
        const today = new Date();
        api.changeView('timeGridDay', today);
        startDateForView = today;
        break;
      }
      case 'week':
        api.changeView('timeGridWeek', startOfWeek(currentStartDate, { weekStartsOn: 1 }));
        break;
      case 'agenda':
        startDateForView = new Date();
        api.changeView('listAgendaCurrentPlusMonth', startDateForView);
        break;
      case 'month':
        api.changeView('dayGridMonth');
        break;
      case 'threeMonths':
        api.changeView('multiThreeMonths');
        break;
      case 'year':
        api.changeView('multiOneYear');
        break;
      default:
        break;
    }

    setState(draft => {
      draft.currentCalendarView = view;
      if (startDateForView instanceof Date) {
        draft.currentStartDate = startDateForView;
      }
    });
  }

  function handleDatesSet(datesSet: DatesSetArg) {
    onVisibleRangeChange?.({
      start: datesSet.start,
      end: datesSet.end,
    });
  }

  return (
    <Box display="flex" flexDirection="column">
      <Box sx={{ '@media print': { display: 'none' } }}>
        <CalendarToolbar
          firstDate={currentStartDate}
          onCalendarViewChange={handleCalendarViewChange}
          onNextClick={handleNextClick}
          onPreviousClick={handlePreviousClick}
          onTodayClick={handleTodayClick}
          onAddEventClick={onAddEventClick}
          currentCalendarView={currentCalendarView}
          toolbarVariant={toolbarVariant}
        />
      </Box>
      <Box sx={{ display: 'none', '@media print': { display: 'block' } }}>
        <Box sx={{ fontSize: '2em', marginBottom: 2 }}>
          {printTitle ? `${printTitle} ` : ''}
          {getHeaderTitle(currentCalendarView, currentStartDate)}
        </Box>
      </Box>
      <Box sx={{ position: 'relative' }}>
        <FullCalendar
          ref={calendarRef}
          headerToolbar={false}
          plugins={[rrulePlugin, timeGridPlugin, dayGridPlugin, listPlugin, multiMonthPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          views={CALENDAR_VIEWS}
          locale="da-DK"
          height="auto"
          firstDay={1}
          nowIndicator
          allDayText="Hele dagen"
          datesSet={handleDatesSet}
          {...fullCalendarProps}
        />
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.6)',
              zIndex: 2,
              '@media print': { display: 'none' },
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
      </Box>
    </Box>
  );
}
